/**
 * SmartSearchCard (VOY-473) — single search card used in both the landing
 * hero pre-search state and the post-search compact header.
 *
 * Refactor of `src/components/landing/v6/HeroSearchCard.tsx`. Differences:
 *   - All state lives in `useSearchState` (VOY-471). No local React state
 *     for query, chips, mode, or Classic fields.
 *   - Classic form fields are controlled, wired through the same picker
 *     popovers as EditablePillRow — no more orphan `defaultValue` inputs.
 *   - The hardcoded "Show price comparison" panel is gone. EditablePillRow
 *     (VOY-472) renders at the bottom in its place.
 *   - Submit forwards the full `SearchState` via `onSubmit`, not just the
 *     bare query string. AI mode runs `parseAIQuery` first; Classic
 *     submits the structured state directly.
 *   - Two visual variants via the `mode` prop: `'hero'` keeps the v6 hero
 *     chrome; `'compact'` collapses the card for the post-search sticky
 *     header (per the Search Plus design). The compact-mode trigger from
 *     the route shell is wired up in VOY-476 + VOY-477 — this ticket only
 *     gives the card the prop.
 *
 * Toolbar buttons (image / voice / chat / web) stay as visual placeholders
 * here. Real onClick handlers land in VOY-474.
 *
 * Visual chrome is provided by `.search-card*` / `.sc-*` rules in
 * `src/styles/voy-v6.css`, all scoped under `.voy-v6`. Render inside a
 * `.voy-v6` ancestor (LandingV6 wraps the whole page).
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { mergeState, useSearchState } from '@/lib/search/useSearchState'
import type { ProductChip, SearchState } from '@/lib/search/types'

import { EditablePillRow } from './EditablePillRow'
import { AirportPicker } from './pickers/AirportPicker'
import { DateRangePicker } from './pickers/DateRangePicker'
import { PassengerPicker } from './pickers/PassengerPicker'
import { CabinPicker } from './pickers/CabinPicker'
import { formatAirport, formatCabinLabel, formatDateRange, formatPassengers } from './format'
import { stateToNaturalQuery } from './queryBuilder'
import { fileToPreviewUrl, parseImage } from '@/lib/search/imageParse'
import { previewTrip } from '@/lib/voy-my-trip'
import '@/styles/voy-my-trip-card.css'
import {
  isVoiceTranscribeSupported,
  startVoiceTranscribe,
  type VoiceSession,
} from '@/lib/search/voiceTranscribe'
// Lazy-loaded: maplibre-gl reads `window.URL.createObjectURL` at module
// load time, which jsdom doesn't provide. Importing lazily keeps tests
// passing AND splits the ~600KB MapLibre bundle out of the main chunk
// so the landing's first paint isn't paying for the globe explorer.
const GlobeSelector = lazy(() => import('@/components/search-plus/globe-selector'))

const ANIMATED_PROMPTS = [
  'One way to Tokyo on July 15',
  'Beach trip from Madrid under $800',
  'Round trip to Paris for 2 weeks',
  'Long weekend in Lisbon with wine tour',
  'Anniversary in Rome, boutique hotel',
]

const PROMPT_INTERVAL_MS = 3400
const PROMPT_FADE_MS = 260

export interface SmartSearchCardProps {
  mode: 'hero' | 'compact'
  /**
   * Called when the user submits the search. Receives the full SearchState
   * after AI parsing (when in AI mode) so the caller can route, persist,
   * or transition State A → State B (VOY-477).
   */
  onSubmit?: (state: SearchState) => void
  /**
   * Optional shared `useSearchState` instance. When omitted (e.g. on the
   * legacy `/search-plus` route), SmartSearchCard creates its own. The
   * unified `/` route (VOY-477) passes its own instance so LandingV6 can
   * (a) hydrate it from URL on mount, (b) read state for tab visibility
   * and search-pipeline params, and (c) avoid double state machines.
   * Pre-VOY-477 callers don't need to change.
   */
  search?: ReturnType<typeof useSearchState>
}

export function SmartSearchCard({ mode, onSubmit, search: sharedSearch }: SmartSearchCardProps) {
  // useSearchState must be called unconditionally to keep hook order
  // stable; we then choose whether to read the local instance or the
  // shared one passed by the parent.
  const localSearch = useSearchState()
  const search = sharedSearch ?? localSearch
  const { state, setField, setMany, toggleChip } = search

  const [promptIdx, setPromptIdx] = useState(0)
  const [promptOpacity, setPromptOpacity] = useState(1)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // VOY-474: toolbar state.
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)

  // VOY-461: Voy My Trip mode — the editable inferred-intent preview + launch.
  const navigate = useNavigate()
  const DEFAULT_VMT_INTENT =
    'A trip Voy assembles from your memory — your home airport, who you travel with, and where you love to go.'
  const [vmtIntent, setVmtIntent] = useState(DEFAULT_VMT_INTENT)
  // Live preview state: true while we fetch the memory-grounded sentence; the
  // edited ref guards against clobbering text the user typed.
  const [vmtPreviewLoading, setVmtPreviewLoading] = useState(false)
  const vmtEditedRef = useRef(false)
  const vmtFetchedRef = useRef(false)
  const vmtPreviewAbortRef = useRef<AbortController | null>(null)

  // When the user switches to Voy My Trip, fetch the live inferred-intent
  // sentence from agent memory (framing-only; no flight/hotel search). Runs
  // once per mount, only while the user hasn't manually edited the box.
  useEffect(() => {
    if (state.mode !== 'voymytrip') return
    if (vmtFetchedRef.current || vmtEditedRef.current) return
    vmtFetchedRef.current = true
    const ac = new AbortController()
    vmtPreviewAbortRef.current = ac
    setVmtPreviewLoading(true)
    const selected = (Object.keys(state.chips) as ProductChip[]).filter((c) => state.chips[c])
    void previewTrip({ components: selected.length ? selected : undefined }, ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        if (res?.preview && !vmtEditedRef.current) setVmtIntent(res.preview)
      })
      .finally(() => { if (!ac.signal.aborted) setVmtPreviewLoading(false) })
    return () => ac.abort()
  }, [state.mode, state.chips])

  const handleVmtIntentChange = useCallback((v: string) => {
    vmtEditedRef.current = true
    setVmtIntent(v)
  }, [])

  const launchVoyMyTrip = useCallback(() => {
    const selected = (Object.keys(state.chips) as ProductChip[]).filter((c) => state.chips[c])
    const params = new URLSearchParams()
    // Only forward the text as an explicit intent if the user actually typed
    // it. The auto-fetched preview is left out so the assemble step re-frames
    // from memory (same path → same trip), avoiding a circular re-feed.
    if (vmtEditedRef.current && vmtIntent.trim()) params.set('intent', vmtIntent.trim())
    if (selected.length) params.set('components', selected.join(','))
    navigate(`/voy-my-trip${params.toString() ? `?${params.toString()}` : ''}`)
  }, [navigate, state.chips, vmtIntent])
  const [isImageProcessing, setIsImageProcessing] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)
  const [isListening, setIsListening] = useState(false)
  const voiceSessionRef = useRef<VoiceSession | null>(null)
  // VOY-379 globe explorer is mounted as an overlay; the Web toolbar
  // button opens it (matches the legacy search-plus.tsx:2613 behavior).
  const [isGlobeOpen, setIsGlobeOpen] = useState(false)
  // Compute once per mount — value never changes for a given browser.
  const voiceSupported = useRef(isVoiceTranscribeSupported()).current

  // Stop any active voice session when the card unmounts.
  useEffect(() => {
    return () => {
      voiceSessionRef.current?.stop()
    }
  }, [])

  // Animated placeholder cycling — only ticks while the AI input is
  // visible. Gated on state.mode so the timer doesn't churn re-renders
  // while the user is in Classic.
  useEffect(() => {
    if (state.mode !== 'ai') return
    intervalRef.current = setInterval(() => {
      setPromptOpacity(0)
      window.setTimeout(() => {
        setPromptIdx((i) => (i + 1) % ANIMATED_PROMPTS.length)
        setPromptOpacity(1)
      }, PROMPT_FADE_MS)
    }, PROMPT_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [state.mode])

  const setMode = (next: 'ai' | 'classic' | 'voymytrip') => {
    if (next === state.mode) return
    if (next === 'ai' && state.query.trim().length === 0) {
      // Classic → AI: auto-generate a query from structured state so the
      // AI input box shows the user's intent rather than going blank.
      const generated = stateToNaturalQuery(state)
      if (generated) setField('query', generated)
    }
    setField('mode', next)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  // "Empty" means the user has nothing meaningful for the search engine
  // to act on — no typed query, no airport pair, no destination. Used to
  // disable the Search buttons so a stray click can't submit the
  // currently-animating placeholder text (fix #3 of the PR #276 review).
  const isEmpty =
    state.query.trim().length === 0 &&
    !state.from?.code &&
    !state.to?.code &&
    !state.departureDate

  const handleSubmit = async () => {
    if (isSubmitting || isEmpty) return
    setIsSubmitting(true)
    try {
      // Build the state we'll forward to onSubmit. Start from current
      // closure state (it's what's been rendered), apply any updates we
      // make this tick, and — for AI mode — merge in the parsed partial
      // returned by parseAIQuery before the React re-render flushes.
      let submitted: SearchState = state
      if (state.mode === 'ai' && state.query.trim().length > 0) {
        try {
          const partial = await search.parseAIQuery(state.query.trim(), {
            inputMode: state.inputMode,
          })
          submitted = mergeState(submitted, partial)
        } catch (err) {
          // Parser failed — surface to console for debugging but still
          // forward the user's intent so the caller can recover.
          // eslint-disable-next-line no-console
          console.error('parseAIQuery failed:', err)
        }
      }
      onSubmit?.(submitted)
    } finally {
      setIsSubmitting(false)
    }
  }

  /* ---- VOY-474 toolbar handlers ------------------------------------ */

  const handleImageButtonClick = () => fileInputRef.current?.click()

  const handleFileSelected = useCallback(
    async (file: File) => {
      setImageError(null)
      setIsImageProcessing(true)
      try {
        const previewUrl = await fileToPreviewUrl(file)
        setImagePreviewUrl(previewUrl)
        const parsed = await parseImage(file)
        // Apply structured fields via setMany (deep-merges passengers/chips).
        // Skip cabin/tripType when undefined — the existing defaults stay.
        setMany({
          ...(parsed.query ? { query: parsed.query } : {}),
          ...(parsed.origin
            ? { from: { code: parsed.origin, label: parsed.originName } }
            : {}),
          ...(parsed.destination
            ? { to: { code: parsed.destination, label: parsed.destinationName } }
            : {}),
          ...(parsed.departureDate ? { departureDate: parsed.departureDate } : {}),
          ...(parsed.returnDate ? { returnDate: parsed.returnDate } : {}),
          ...(parsed.travelers
            ? {
                passengers: {
                  adults: parsed.travelers.adults ?? state.passengers.adults,
                  children: parsed.travelers.children ?? state.passengers.children,
                  ...(parsed.travelers.childrenAges?.length
                    ? { childrenAges: parsed.travelers.childrenAges }
                    : {}),
                },
              }
            : {}),
          ...(parsed.tripType ? { tripType: parsed.tripType } : {}),
          ...(parsed.cabin ? { cabin: parsed.cabin } : {}),
          source: 'image-search',
          imageSearch: { url: previewUrl, description: parsed.imageSearch.description },
        })
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Image search failed'
        setImageError(msg)
        setImagePreviewUrl(null)
        // eslint-disable-next-line no-console
        console.error('[SmartSearchCard] parseImage failed:', err)
      } finally {
        setIsImageProcessing(false)
      }
    },
    [setMany, state.passengers.adults, state.passengers.children],
  )

  const clearImage = () => {
    // Note: do NOT stop the active voice session here — image and voice
    // are orthogonal concerns (per PR #277 review fix #1). The cleanup
    // effect on unmount already tears down voice; mid-dictation users
    // tapping × shouldn't lose their transcript.
    setImagePreviewUrl(null)
    setImageError(null)
    setField('imageSearch', undefined)
    if (state.source === 'image-search') setField('source', undefined)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleVoiceToggle = () => {
    if (!voiceSupported) return
    if (isListening) {
      voiceSessionRef.current?.stop()
      return
    }
    setImageError(null)
    setField('source', 'voice-search')
    setIsListening(true)
    voiceSessionRef.current = startVoiceTranscribe({
      onTranscript: (transcript) => {
        setField('query', transcript)
      },
      onEnd: (finalTranscript) => {
        setIsListening(false)
        voiceSessionRef.current = null
        // Auto-parse on stop if we picked anything up. Don't auto-submit
        // — leave that as an explicit Search click so the user can
        // edit before sending.
        if (finalTranscript.trim().length > 0) {
          void search.parseAIQuery(finalTranscript.trim(), {
            inputMode: state.inputMode,
          }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[SmartSearchCard] voice parse failed:', err)
          })
        }
      },
      onError: (msg) => {
        setIsListening(false)
        voiceSessionRef.current = null
        // eslint-disable-next-line no-console
        console.error('[SmartSearchCard] voice error:', msg)
      },
    })
  }

  const handleChatClick = () => {
    // The chat icon is the "default text-typing mode" indicator. Per
    // the v6 design it's always active visually; clicking it is a
    // no-op interaction-wise. Kept as a button (vs span) for
    // consistent semantics with the other toolbar entries.
    if (state.inputMode !== 'chat') setField('inputMode', 'chat')
  }

  /**
   * Open the VOY-379 globe destination picker. The legacy
   * search-plus.tsx triggered it from the same Globe icon (line 2613).
   * Replaces the inputMode 'web' toggle I initially wired off the
   * VOY-474 ticket text — the existing destination-picker is the
   * canonical behavior here.
   */
  const handleGlobeClick = () => {
    setIsGlobeOpen(true)
  }

  const handleGlobeSelectDestination = (location: string) => {
    // Mirror the legacy search-plus handler: set the AI query to a
    // "Round trip to X" phrase and switch to AI mode. When the user
    // clicks Search, parseAIQuery will fill in `state.to.code` (the
    // IATA) — pre-writing an `AirportRef` with an empty code here
    // would break downstream IATA-dependent code.
    setMany({
      query: `Round trip to ${location}`,
      mode: 'ai',
      source: 'globe-explorer',
    })
    setIsGlobeOpen(false)
  }

  return (
    <div className={`search-card${mode === 'compact' ? ' compact' : ''}`} data-mode={mode}>
      <ModeToggle mode={state.mode} onChange={setMode} />

      {state.mode === 'ai' ? (
        <AIInputBlock
          value={state.query}
          onChange={(v) => setField('query', v)}
          onSubmit={handleSubmit}
          promptIdx={promptIdx}
          promptOpacity={promptOpacity}
        />
      ) : state.mode === 'voymytrip' ? (
        <InferredIntentBlock value={vmtIntent} onChange={handleVmtIntentChange} loading={vmtPreviewLoading} />
      ) : (
        <ClassicForm search={search} onSubmit={handleSubmit} isSubmitting={isSubmitting} isEmpty={isEmpty} />
      )}

      {state.mode === 'ai' ? (
        <>
          <ChipRow chips={state.chips} onToggle={toggleChip} />
          <Toolbar
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isEmpty={isEmpty}
            inputMode={state.inputMode}
            isListening={isListening}
            voiceSupported={voiceSupported}
            isImageProcessing={isImageProcessing}
            isGlobeOpen={isGlobeOpen}
            onImageClick={handleImageButtonClick}
            onVoiceToggle={handleVoiceToggle}
            onChatClick={handleChatClick}
            onGlobeClick={handleGlobeClick}
          />
        </>
      ) : null}

      {/* VOY-461: Voy My Trip — component pills drive scope; the single action is
          the "Voy My Trip" launch. (No "Search" button here: VMT has no typed
          query/airports/dates so a classic search would be a no-op — the launch
          assembles the trip from memory instead.) */}
      {state.mode === 'voymytrip' ? (
        <>
          <ChipRow chips={state.chips} onToggle={toggleChip} />
          <div className="sc-vmt-controls">
            <button type="button" className="sc-vmt-go" onClick={launchVoyMyTrip}>
              <span className="sc-vmt-go__star" aria-hidden="true">✦</span> Voy My Trip
            </button>
          </div>
        </>
      ) : null}

      {imagePreviewUrl || imageError || isImageProcessing ? (
        <ImagePreview
          url={imagePreviewUrl}
          processing={isImageProcessing}
          error={imageError}
          onRemove={clearImage}
        />
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        // Deliberately excludes HEIC/HEIF — the backend's vision
        // service only accepts jpeg/png/webp/gif (see
        // visionClient.js:ALLOWED_MIME_TYPES). iPhone users on Safari
        // who pick a .heic from "All Photos" would get a 400 from the
        // backend MIME validator; filtering them out of the picker
        // surfaces the limitation earlier. A follow-up ticket could
        // transcode HEIC → JPEG client-side via canvas before upload.
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleFileSelected(file)
        }}
      />

      <EditablePillRow searchState={search} />

      {isGlobeOpen ? (
        <Suspense fallback={null}>
          <GlobeSelector
            onClose={() => setIsGlobeOpen(false)}
            onSelectDestination={handleGlobeSelectDestination}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* AI / Classic toggle                                                 */
/* ------------------------------------------------------------------ */

function ModeToggle({
  mode,
  onChange,
}: {
  mode: 'ai' | 'classic' | 'voymytrip'
  onChange: (next: 'ai' | 'classic' | 'voymytrip') => void
}) {
  return (
    <div className="sc-toggle" role="tablist" aria-label="Search mode">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'ai'}
        className={mode === 'ai' ? 'active' : ''}
        onClick={() => onChange('ai')}
      >
        AI
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'classic'}
        className={mode === 'classic' ? 'active' : ''}
        onClick={() => onChange('classic')}
      >
        Classic
      </button>
      {/* VOY-461: third mode — Voy assembles a personalized trip from memory. */}
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'voymytrip'}
        className={mode === 'voymytrip' ? 'active vmt' : 'vmt'}
        onClick={() => onChange('voymytrip')}
      >
        Voy My Trip
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* VOY-461 — Voy My Trip inferred-intent preview                        */
/* ------------------------------------------------------------------ */

function InferredIntentBlock({
  value,
  onChange,
  loading,
}: {
  value: string
  onChange: (v: string) => void
  loading?: boolean
}) {
  return (
    <div className="infer">
      <span className="infer-tag">
        {loading ? 'Reading your memory…' : 'Inferred from your memory · editable'}
      </span>
      <textarea
        className="infer-txt"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        aria-label="Inferred trip intent — editable"
        aria-busy={loading}
      />
      <div className="infer-hint">
        <span className="infer-hint__star" aria-hidden="true">✦</span>
        Tap to edit · Voy drafted this from your home airport, travel companion, and past trips
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* AI input block                                                       */
/* ------------------------------------------------------------------ */

function AIInputBlock({
  value,
  onChange,
  onSubmit,
  promptIdx,
  promptOpacity,
}: {
  value: string
  onChange: (next: string) => void
  onSubmit: () => void
  promptIdx: number
  promptOpacity: number
}) {
  // Single always-mounted input — the prior dual-input setup
  // (visible / off-screen depending on emptiness) caused focus loss
  // on the first keystroke when the empty→non-empty swap unmounted
  // the just-focused element. Now the animated prompt is rendered as
  // an absolutely-positioned fading overlay; `pointer-events: none`
  // lets clicks pass through to the input. Per fix #2 of the PR #276
  // review.
  const showOverlay = value.length === 0
  return (
    <label className="sc-input" htmlFor="sc-ai-input">
      <input
        id="sc-ai-input"
        aria-label="Search query"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onSubmit()
          }
        }}
        style={{
          flex: 1,
          background: 'transparent',
          border: 0,
          outline: 'none',
          color: 'inherit',
          fontFamily: 'inherit',
          fontSize: 'inherit',
        }}
      />
      {showOverlay ? (
        <span
          className="prompt-text"
          style={{
            opacity: promptOpacity,
            position: 'absolute',
            left: 20,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          {ANIMATED_PROMPTS[promptIdx]}
        </span>
      ) : null}
    </label>
  )
}

/* ------------------------------------------------------------------ */
/* Chip row                                                              */
/* ------------------------------------------------------------------ */

interface ChipDef {
  key: ProductChip
  label: string
  icon: React.ReactNode
}

const CHIPS: ChipDef[] = [
  {
    key: 'flights',
    label: 'Flights',
    icon: (
      <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
      </svg>
    ),
  },
  {
    key: 'hotels',
    label: 'Hotels',
    icon: (
      <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9v12h18V9M3 9l9-7 9 7M9 21V12h6v9" />
      </svg>
    ),
  },
  {
    key: 'itinerary',
    label: 'Itinerary',
    icon: (
      <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
]

function ChipRow({
  chips,
  onToggle,
}: {
  chips: Record<ProductChip, boolean>
  onToggle: (chip: ProductChip) => void
}) {
  return (
    <div className="sc-chips" role="group" aria-label="Product types">
      {CHIPS.map((chip) => (
        <button
          key={chip.key}
          type="button"
          aria-pressed={chips[chip.key]}
          className={`sc-chip${chips[chip.key] ? ' on' : ''}`}
          onClick={() => onToggle(chip.key)}
        >
          {chip.icon}
          {chip.label}
          <svg
            className="chk"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </button>
      ))}
      <button
        type="button"
        className="sc-chip dis"
        disabled
        aria-disabled="true"
        title="Coming soon"
      >
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 17h14M5 17a2 2 0 1 1-2-2h0M19 17a2 2 0 1 0 2-2h0M3 15l2-7h14l2 7M7 11h10" />
        </svg>
        Cars<span className="soon">Soon</span>
      </button>
      <button
        type="button"
        className="sc-chip dis"
        disabled
        aria-disabled="true"
        title="Coming soon"
      >
        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l2.5 6.5L21 9l-5 4.5L17.5 21 12 17 6.5 21 8 13.5 3 9l6.5-.5L12 2z" />
        </svg>
        Activities<span className="soon">Soon</span>
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Toolbar                                                              */
/* ------------------------------------------------------------------ */

function Toolbar({
  onSubmit,
  isSubmitting,
  isEmpty,
  inputMode,
  isListening,
  voiceSupported,
  isImageProcessing,
  isGlobeOpen,
  onImageClick,
  onVoiceToggle,
  onChatClick,
  onGlobeClick,
}: {
  onSubmit: () => void
  isSubmitting: boolean
  isEmpty: boolean
  inputMode: 'chat' | 'web'
  isListening: boolean
  voiceSupported: boolean
  isImageProcessing: boolean
  isGlobeOpen: boolean
  onImageClick: () => void
  onVoiceToggle: () => void
  onChatClick: () => void
  onGlobeClick: () => void
}) {
  return (
    <div className="sc-tools">
      <div className="sc-tools-l">
        <button
          type="button"
          className="sc-tool"
          title={isImageProcessing ? 'Analyzing image…' : 'Attach image'}
          aria-label="Attach image"
          onClick={onImageClick}
          disabled={isImageProcessing}
          aria-busy={isImageProcessing}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </button>
        <button
          type="button"
          className={`sc-tool${isListening ? ' listening' : ''}`}
          title={
            !voiceSupported
              ? 'Voice not supported in this browser'
              : isListening
                ? 'Stop listening'
                : 'Voice input'
          }
          // VOY-474 PR #277 review fix #5: when a button is disabled,
          // screen readers don't announce the `title` attribute — so
          // unsupported-browser users wouldn't hear why the mic is
          // off. Fold the "not supported" status into aria-label
          // directly when disabled.
          aria-label={
            !voiceSupported
              ? 'Voice input not supported in this browser'
              : isListening
                ? 'Stop voice input'
                : 'Voice input'
          }
          aria-pressed={isListening}
          onClick={onVoiceToggle}
          disabled={!voiceSupported}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M19 10a7 7 0 0 1-14 0M12 18v3" />
          </svg>
        </button>
        <button
          type="button"
          className={`sc-tool${inputMode === 'chat' ? ' active' : ''}`}
          title="Chat mode"
          aria-label="Chat mode"
          aria-pressed={inputMode === 'chat'}
          onClick={onChatClick}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        <button
          type="button"
          className={`sc-tool${isGlobeOpen ? ' active' : ''}`}
          title="Explore destinations on a globe"
          aria-label="Open globe explorer"
          aria-haspopup="dialog"
          aria-expanded={isGlobeOpen}
          onClick={onGlobeClick}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
          </svg>
        </button>
      </div>
      <div className="sc-tools-r">
        <button type="button" className="sc-preview">
          Preview
        </button>
        <button
          type="button"
          className="sc-go"
          onClick={onSubmit}
          disabled={isSubmitting || isEmpty}
          aria-busy={isSubmitting}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Search
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Classic form                                                          */
/* ------------------------------------------------------------------ */

function ClassicForm({
  search,
  onSubmit,
  isSubmitting,
  isEmpty,
}: {
  search: ReturnType<typeof useSearchState>
  onSubmit: () => void
  isSubmitting: boolean
  isEmpty: boolean
}) {
  const { state, setField, setMany } = search

  return (
    <div className="sc-classic">
      <div className="sc-classic-grid">
        <ClassicField label="From" picker={<AirportPicker value={state.from} onChange={(v) => setField('from', v)} />}>
          {formatAirport(state.from)}
        </ClassicField>

        <ClassicField label="To" picker={<AirportPicker value={state.to} onChange={(v) => setField('to', v)} />}>
          {formatAirport(state.to)}
        </ClassicField>

        <ClassicField
          label="Dates"
          picker={
            <DateRangePicker
              departureDate={state.departureDate}
              returnDate={state.returnDate}
              tripType={state.tripType}
              onChange={(next) => setMany(next)}
            />
          }
        >
          {formatDateRange(state.departureDate, state.returnDate, state.tripType)}
        </ClassicField>

        <ClassicField
          label="Passengers"
          picker={<PassengerPicker value={state.passengers} onChange={(v) => setField('passengers', v)} />}
        >
          {formatPassengers(state.passengers)}
        </ClassicField>

        <ClassicField label="Cabin" picker={<CabinPicker value={state.cabin} onChange={(v) => setField('cabin', v)} />}>
          {formatCabinLabel(state.cabin)}
        </ClassicField>
      </div>
      <div className="sc-classic-foot">
        <span className="sc-classic-note">
          Same engine, same real price, just the familiar form.
        </span>
        <button
          type="button"
          className="sc-go"
          onClick={onSubmit}
          disabled={isSubmitting || isEmpty}
          aria-busy={isSubmitting}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4-4" />
          </svg>
          Search
        </button>
      </div>
    </div>
  )
}

function ClassicField({
  label,
  picker,
  children,
}: {
  label: string
  picker: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Bare `aria-label={label}` (not "Edit X") so it doesn't collide
            with EditablePillRow's `aria-label="Edit cabin"` etc. when both
            render in Classic mode. */}
        <button type="button" className="cfield" aria-label={label}>
          <span className="cf-lbl">{label}</span>
          <span className="cf-val">{children}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        {picker}
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/* Image preview block (VOY-474)                                       */
/* ------------------------------------------------------------------ */

function ImagePreview({
  url,
  processing,
  error,
  onRemove,
}: {
  url: string | null
  processing: boolean
  error: string | null
  onRemove: () => void
}) {
  return (
    <div className="sc-image-preview" role="status" aria-live="polite">
      {url ? (
        <div className="sc-image-thumb-wrap">
          <img src={url} alt="Search image" className="sc-image-thumb" />
          <button
            type="button"
            className="sc-image-remove"
            onClick={onRemove}
            aria-label="Remove image"
            title="Remove image"
          >
            <X aria-hidden="true" />
          </button>
        </div>
      ) : null}
      <div className="sc-image-status">
        {processing ? <span className="sc-image-spinner">Analyzing image…</span> : null}
        {error ? <span className="sc-image-error">{error}</span> : null}
      </div>
    </div>
  )
}
